import { useState } from "react";
import { IconDeviceMobile, IconShare2, IconSquareRoundedPlus } from "@tabler/icons-react";
import { ResponsiveModal } from "@/components/responsive-modal";
import { useAddToHomescreen } from "@/hooks/use-add-to-homescreen";

/** "Add to Home Screen" affordance. Renders nothing when the app already
 *  runs from the homescreen or the browser has no install path. On Chromium
 *  it fires the real install prompt; on iOS it explains the Share-sheet
 *  steps (Safari has no prompt API). */
export function AddToHomescreen({ variant }: { variant: "sidebar" | "topbar" }) {
  const { mode, promptInstall } = useAddToHomescreen();
  const [showInstructions, setShowInstructions] = useState(false);

  if (!mode) return null;

  const handleClick = () => {
    if (mode === "prompt") void promptInstall();
    else setShowInstructions(true);
  };

  return (
    <>
      {variant === "sidebar" ? (
        // Owns its pinned-row chrome so a null render leaves no empty strip.
        <div className="shrink-0 border-t border-sidebar-border px-3 py-3">
          <button
            onClick={handleClick}
            className="flex items-center justify-center gap-2 w-full px-3 py-2 text-[13px]
              font-semibold rounded-md transition-colors bg-emerald-600 text-white
              hover:bg-emerald-500 active:bg-emerald-700 shadow-sm"
          >
            <IconDeviceMobile className="h-4 w-4" />
            Add to Home Screen
          </button>
        </div>
      ) : (
        <button
          onClick={handleClick}
          className="flex items-center gap-1.5 rounded-full bg-emerald-600 text-white
            pl-2.5 pr-3 py-1 text-xs font-semibold shadow-sm transition-transform
            hover:bg-emerald-500 active:scale-95"
          aria-label="Add to Home Screen"
        >
          <IconDeviceMobile className="h-3.5 w-3.5" />
          Install
        </button>
      )}

      {mode === "ios-instructions" && (
        <ResponsiveModal
          open={showInstructions}
          onOpenChange={setShowInstructions}
          title="Add to Home Screen"
        >
          <div className="space-y-4 pb-2">
            <p className="text-sm text-muted-foreground">
              Safari installs the app from its Share menu:
            </p>
            <div className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-semibold shrink-0">
                1
              </span>
              <span className="text-sm">
                Tap the <IconShare2 className="inline h-4 w-4 mx-0.5 align-text-bottom" />{" "}
                <span className="font-medium">Share</span> button in the toolbar
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-semibold shrink-0">
                2
              </span>
              <span className="text-sm">
                Scroll down and tap{" "}
                <IconSquareRoundedPlus className="inline h-4 w-4 mx-0.5 align-text-bottom" />{" "}
                <span className="font-medium">Add to Home Screen</span>
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              The app then opens full-screen from its own icon, like a native app.
            </p>
          </div>
        </ResponsiveModal>
      )}
    </>
  );
}
